// Map a Genesys langTag, or a wizard uiLang, onto one of the nine Insights
// languages. The install wizard copies this file and test/matchGenesysLanguage.test.ts;
// keep the two copies identical.
//
// Hyphen, underscore, and camelCase are the same tag (`en-us`, `en_US`, `enUS`).
// `pt-br` is Portuguese (Brazil), `pt-pt` is Portuguese (Portugal). Bare `pt`
// and any other Portuguese region are Brazil. That bare-`pt` rule is for
// Genesys and link codes only — an old saved `pt` stays Portugal
// (normalizeLanguageCode). Other regions use the base language. `iw` is Hebrew.
// Anything unrecognized returns null; the caller uses English.
//
// The code union matches LanguageCode in types.ts. It is repeated here so this
// file has no imports and can be copied unchanged.

type MatchedLanguage = 'en' | 'es' | 'fr' | 'de' | 'it' | 'pt-BR' | 'pt-PT' | 'pl' | 'he';

const BASE_TO_LANGUAGE: Readonly<Record<string, MatchedLanguage>> = {
  en: 'en',
  es: 'es',
  fr: 'fr',
  de: 'de',
  it: 'it',
  pl: 'pl',
  he: 'he',
  iw: 'he',
};

function canonicalize(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  // A lowercase/uppercase boundary is a separator (`enUS` → `en-US`).
  // Glued lowercase (`enus`, `plus`) is left as-is so a word is not split
  // into a language plus a region.
  const hyphenated = trimmed
    .replace(/_/g, '-')
    .replace(/([a-z\d])([A-Z])/g, '$1-$2')
    .toLowerCase()
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  if (!hyphenated.includes('-')) {
    const numericRegion = hyphenated.match(/^([a-z]{2})(\d{3})$/);
    if (numericRegion) return `${numericRegion[1]}-${numericRegion[2]}`;
  }
  return hyphenated;
}

/** First region subtag: 2 letters or 3 digits. Script subtags (4 letters) are skipped. */
function regionSubtag(parts: readonly string[]): string | undefined {
  for (let i = 1; i < parts.length; i++) {
    const part = parts[i];
    if (part && (/^[a-z]{2}$/.test(part) || /^\d{3}$/.test(part))) return part;
  }
  return undefined;
}

function matchPortuguese(parts: readonly string[]): MatchedLanguage {
  // Only exact Portugal. Bare `pt` and pt-BR, pt-AO, and the rest are Brazil.
  return regionSubtag(parts) === 'pt' ? 'pt-PT' : 'pt-BR';
}

export function matchGenesysLanguage(raw: string | null | undefined): MatchedLanguage | null {
  if (raw == null) return null;
  const tag = canonicalize(raw);
  if (!tag) return null;
  const parts = tag.split('-').filter((part) => part.length > 0);
  const base = parts[0];
  if (!base) return null;
  if (base === 'pt') return matchPortuguese(parts);
  return BASE_TO_LANGUAGE[base] ?? null;
}
